import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ClipSelectorPlaybackManagerContextType,
  ClipSelectorPlaybackManagerProviderProps,
  PlayerElement,
  UseClipSelectorPlaybackManagerBoundResult,
  UseClipSelectorPlaybackManagerOptions,
  UseClipSelectorPlaybackManagerUnboundResult,
} from './types';

const ClipSelectorPlaybackManagerContext = React.createContext<
  ClipSelectorPlaybackManagerContextType | undefined
>(undefined);

export const ClipSelectorPlaybackManagerProvider: React.FC<ClipSelectorPlaybackManagerProviderProps> = ({
  children,
}) => {
  const [currentlyPlayingClipId, setCurrentlyPlayingClipId] = useState<
    number | null
  >(null);
  const playerRefs = useRef<Map<number, PlayerElement>>(new Map());

  const registerPlayer = useCallback(
    (clipId: number, player: PlayerElement) => {
      playerRefs.current.set(clipId, player);
    },
    [],
  );

  const unregisterPlayer = useCallback((clipId: number) => {
    playerRefs.current.delete(clipId);
  }, []);

  const stopPlaying = useCallback((clipId: number) => {
    const player = playerRefs.current.get(clipId);

    if (player) {
      player.pause();
      setCurrentlyPlayingClipId(null);
    }
  }, []);

  const startPlaying = useCallback(
    (clipId: number) => {
      // Stop any currently playing video
      if (currentlyPlayingClipId && currentlyPlayingClipId !== clipId) {
        stopPlaying(currentlyPlayingClipId);
      }

      // Start the new video
      const player = playerRefs.current.get(clipId);

      if (player) {
        player.play();
        setCurrentlyPlayingClipId(clipId);
      }
    },
    [currentlyPlayingClipId, stopPlaying],
  );

  const stopAll = useCallback(() => {
    // Pause all videos
    playerRefs.current.forEach(player => {
      player?.pause();
    });
    setCurrentlyPlayingClipId(null);
  }, []);

  const isPlaying = useCallback(
    (clipId: number) => {
      return currentlyPlayingClipId === clipId;
    },
    [currentlyPlayingClipId],
  );

  const value = useMemo(
    () => ({
      currentlyPlayingClipId,
      startPlaying,
      stopPlaying,
      stopAll,
      isPlaying,
      registerPlayer,
      unregisterPlayer,
    }),
    [
      currentlyPlayingClipId,
      startPlaying,
      stopPlaying,
      stopAll,
      isPlaying,
      registerPlayer,
      unregisterPlayer,
    ],
  );

  return (
    <ClipSelectorPlaybackManagerContext.Provider value={value}>
      {children}
    </ClipSelectorPlaybackManagerContext.Provider>
  );
};

export function useClipSelectorPlaybackManager(
  opts: Required<UseClipSelectorPlaybackManagerOptions>,
): UseClipSelectorPlaybackManagerBoundResult;
export function useClipSelectorPlaybackManager(
  opts?: UseClipSelectorPlaybackManagerOptions,
): UseClipSelectorPlaybackManagerUnboundResult;
export function useClipSelectorPlaybackManager(
  options?: UseClipSelectorPlaybackManagerOptions,
):
  | UseClipSelectorPlaybackManagerBoundResult
  | UseClipSelectorPlaybackManagerUnboundResult {
  const context = useContext(ClipSelectorPlaybackManagerContext);

  if (context === undefined) {
    throw new Error(
      'useClipSelectorPlaybackManager must be used within an ClipSelectorPlaybackManagerProvider',
    );
  }

  const { clipId } = options || {};

  const boundStartPlaying = useCallback(() => {
    context.startPlaying(clipId);
  }, [clipId, context]);

  const boundStopPlaying = useCallback(() => {
    context.stopPlaying(clipId);
  }, [clipId, context]);

  const boundIsPlaying = useCallback(() => context.isPlaying(clipId), [
    clipId,
    context,
  ]);

  const boundRegisterPlayer = useCallback(
    (player: PlayerElement) => {
      context.registerPlayer(clipId, player);
    },
    [clipId, context],
  );

  const boundUnregisterPlayer = useCallback(() => {
    context.unregisterPlayer(clipId);
  }, [clipId, context]);

  const boundContext = useMemo(
    () =>
      !clipId
        ? undefined
        : {
            ...context,
            startPlaying: boundStartPlaying,
            stopPlaying: boundStopPlaying,
            isPlaying: boundIsPlaying,
            isClipPlaying: context.isPlaying(clipId),
            registerPlayer: boundRegisterPlayer,
            unregisterPlayer: boundUnregisterPlayer,
          },
    [
      boundIsPlaying,
      boundRegisterPlayer,
      boundStartPlaying,
      boundStopPlaying,
      boundUnregisterPlayer,
      clipId,
      context,
    ],
  );

  return clipId !== undefined ? boundContext : context;
}
